import React, { FC, useCallback, useState } from 'react';
import { Modal, Radio, RadioGroup, Textfield } from '@workboard/wobo-ui';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { AggregatedProvider, useActionItem } from '@workboard/action-item';
import { FormData, StopAfterType, useRepeatAiForm } from './useRepeatAiForm';
import { ItemContainer, InlineSelectField } from '../form/InlineField';
import { RepeatField } from '../Repeat/RepeatField';
import { TimeUnitField } from '../Repeat/TimeUnitField';
import * as testIds from './testIds';
import { Repeat } from '../Repeat/model';
import { getGlobalConfig } from '../global/ConfigProvider';
import { onMessage } from '../global/onMessage';

const Wrapper = styled.div`
  height: 265px;
  width: 500px;
`;

type RepeatData = Partial<FormData>;

interface IProps {
  aiId: string;
  initialData?: RepeatData;
  onClose?: () => void;
  onSubmit?: (data: FormData) => void;
}

export const RepeatAiModal: FC<IProps> = ({
  aiId,
  onClose,
  onSubmit,
  initialData,
}) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(true);
  const { submitForm, getFieldProps, isProcessing, formErrors, values } =
    useRepeatAiForm(aiId, initialData);

  const closeCallback = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose]);

  const submitCallback = useCallback(async () => {
    try {
      await submitForm();
      setOpen(false);
      onSubmit?.(values);
      setTimeout(() => onClose?.(), 5000);
    } catch (e) {
      console.error(e);
    }
  }, [onClose, onSubmit, submitForm, values]);

  const stopAfterDisabled = values.repeat === Repeat.NoRepeat;
  const stopAfterSubFormDisabled =
    values.stopAfterType !== StopAfterType.Some || stopAfterDisabled;
  const { actionItem } = useActionItem(aiId);
  const actionTypeName = (actionItem?.typeAttributes?.label ?? 'action').toLowerCase();
  const title = `${t("Repeat {{actionTypeName}}", {actionTypeName})}`;
  const anText = ['risk', 'milestone'].includes(actionTypeName) ? 'A' : 'An';
  return (
    <Modal
      disableSubmit={Object.values(formErrors).length || isProcessing}
      dismissLabel={t('Cancel')}
      isOpen={isOpen}
      onDismiss={closeCallback}
      onSubmit={submitCallback}
      required={false}
      submitLabel={t('Save')}
      title={title}
    >
      <Wrapper>
        <p>{t('actionType.schedulingExplanation', { actionTypeName })}</p>
        <ItemContainer data-testid={testIds.repeatField} items={2}>
          <InlineSelectField>
            <RepeatField {...getFieldProps('repeat', (val) => parseInt(val))} />
          </InlineSelectField>
        </ItemContainer>
        <ItemContainer data-testid={testIds.stopAfterField}>
          <RadioGroup
            label={t('Stop repeating it after')}
            {...getFieldProps('stopAfterType', (val) => parseInt(val))}
            isDisabled={stopAfterDisabled}
          >
            <Radio
              label={t('Never')}
              value={StopAfterType.Never}
              isDisabled={stopAfterDisabled}
            />
            <ItemContainer items={4}>
              <Radio
                label={t('After')}
                value={StopAfterType.Some}
                isDisabled={stopAfterDisabled}
              />
              <Textfield
                type="number"
                {...getFieldProps('stopAfterUnits', (e) =>
                  parseInt(e.target?.value)
                )}
                isDisabled={stopAfterSubFormDisabled}
              />
              <TimeUnitField
                {...getFieldProps('stopAfterUnit')}
                isDisabled={stopAfterSubFormDisabled}
              />
              <div />
            </ItemContainer>
          </RadioGroup>
        </ItemContainer>
      </Wrapper>
    </Modal>
  );
};

export const WrappedRepeatAiModal: FC<IProps> = ({
  aiId,
  onClose,
  onSubmit,
}) => (
  <AggregatedProvider
    id={aiId}
    config={getGlobalConfig()}
    onMessage={onMessage}
  >
    <RepeatAiModal aiId={aiId} onClose={onClose} onSubmit={onSubmit} />
  </AggregatedProvider>
);
